import React, { useState, useEffect, useMemo } from 'react';
import '../../../../../../Stylesheets/Dashboard/Tabs/Expenses/Expenses.css'; // For styling and animation
import expenseIllustration from '../../../../../../assets/illustration/no-expense.png'; // use your image path
import AddExpenseModal from './Component/AddExpenseModal';
import ViewExpenseDrawer from './Component/ViewExpenseDrawer'
import UploadBillModal from './Component/UploadBillModal';
import ViewReportModal from './Component/ViewReportModal';
import EditExpenseModal from './Component/EditExpenseModal'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  fetchExpensesByProject,
  fetchExpenseOverview,
  fetchExpenseById,
  createExpense,
  uploadExpenseBill,
  deleteExpense,
  editExpense,
  clearExpenseDetails,
  deleteExpenseBill,
  fetchRecentExpenseActivity,
  fetchMoreExpenseActivity,
  resetExpenseActivity,
} from '../../../../../../Redux/Projects/Expense/expenseSlice'; // adjust path if needed

import axiosWithAuth from '../../../../../../Redux/UtilsRedux/axiosWithAuth';
import { API_ROUTES } from '../../../../../../Redux/Constants/apiRoutes';

import { storage } from '../../../../../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

import EmptyState from "../../../../../../Components/Common/EmptyState";
import { showFeedback } from '../../../../../../Redux/UI/uiFeedbackSlice';



import { FaPlus, FaUpload, FaFileAlt, FaEye, FaPen, FaTrash } from 'react-icons/fa';

const INR = (n) => `₹${(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });


const Expenses = ({ currentProjectId }) => {

  console.log(currentProjectId)

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 🔗 Redux state
  const {
    items: expenses,
    loading,
    overview,
    overviewLoading,
    expenseDetails,
    expenseDetailsLoading,
    submitting,
    expenseActivity,
    expenseActivityNextCursor,
    expenseActivityLoading,
    exportingReport,
  } = useSelector((s) => s.expenses);

  // 🧠 Local UI state
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [expenseForUpload, setExpenseForUpload] = useState(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);

  const [showReportModal, setShowReportModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [category, setCategory] = useState('all');
  const [format, setFormat] = useState('summary');

  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('date');   // 'date' | 'amount' | 'category'
  const [sortDir, setSortDir] = useState('desc');   // 'asc' | 'desc'


  // top of component state
  const [showEditModal, setShowEditModal] = useState(false);
  const [expenseForEdit, setExpenseForEdit] = useState(null);


  // NEW: boot loader gate
  const [initialLoad, setInitialLoad] = useState(true);

  // 🚀 Fetch on mount / project change
  useEffect(() => {
    if (!currentProjectId) return;

    let alive = true;
    setInitialLoad(true);

    const p1 = dispatch(
      fetchExpensesByProject({ projectId: currentProjectId, page: 1, pageSize: 50 })
    ).unwrap().catch(() => { });

    const p2 = dispatch(
      fetchExpenseOverview(currentProjectId)
    ).unwrap().catch(() => { });


     // reset local + redux activity before showing UI
    dispatch(resetExpenseActivity());


    const p3 = dispatch(
      fetchRecentExpenseActivity({ projectId: currentProjectId, limit: 6 })
    ).unwrap().catch(() => { });

   

    Promise.allSettled([p1, p2, p3]).then(() => {
      if (!alive) return;
      setTimeout(() => alive && setInitialLoad(false), 250);
    });

    return () => { alive = false; };
  }, [currentProjectId, dispatch]);


  const notify = (type, title, message) =>
    dispatch(showFeedback({ type, title, message }));



  useEffect(() => {
    if (!currentProjectId) return;

    let timer;
    const T = 12000; // 12s
    const tick = () => {
      // Pause if tab hidden to save resources
      if (document.visibilityState === 'visible') {
        dispatch(fetchRecentExpenseActivity({ projectId: currentProjectId, limit: 6 }));
      }
    };

    timer = setInterval(tick, T);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [currentProjectId, dispatch]);


  // 🔎 Filter + sort
  const filteredExpenses = useMemo(() => {
    const list = Array.isArray(expenses) ? expenses : [];
    const q = searchQuery.trim().toLowerCase();

    let rows = q
      ? list.filter((e) =>
        (e.category || '').toLowerCase().includes(q) ||
        (e.notes || '').toLowerCase().includes(q)
      )
      : list.slice();

    const mult = sortDir === 'asc' ? 1 : -1;

    rows.sort((a, b) => {
      if (sortKey === 'amount') {
        const av = Number(a.amount) || 0;
        const bv = Number(b.amount) || 0;
        return mult * (av - bv);
      }
      if (sortKey === 'date') {
        const av = a.date ? new Date(a.date).getTime() : 0;
        const bv = b.date ? new Date(b.date).getTime() : 0;
        return mult * (av - bv);
      }
      if (sortKey === 'category') {
        const av = (a.category || '').toLowerCase();
        const bv = (b.category || '').toLowerCase();
        return mult * av.localeCompare(bv);
      }
      return 0;
    });

    return rows;
  }, [expenses, searchQuery, sortKey, sortDir]);


  // put near top inside component
  const refreshActivity = () =>
    dispatch(fetchRecentExpenseActivity({ projectId: currentProjectId, limit: 6 }));


  // ➕ Add Expense
  const handleAddExpense = async (payload) => {
    // payload contains billFile (File or null)
    const { billFile, ...rest } = payload;

    // 1) create expense first
    const res = await dispatch(createExpense({ projectId: currentProjectId, ...rest }));
    const created = res.payload; // from thunk: expense doc with _id
    if (!created?._id) return;

    // 2) if a file was selected, upload to Firebase then attach
    if (billFile) {
      const ext = billFile.name.split('.').pop() || '';
      const path = `expenses/${currentProjectId}/${created._id}/${Date.now()}.${ext}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, billFile);
      const url = await getDownloadURL(fileRef);

      await dispatch(uploadExpenseBill({
        expenseId: created._id,
        fileUrl: url,
        fileName: billFile.name,
        mimeType: billFile.type,
        billNumber: rest.billNumber, // pass along if set
      }));
    }

    // 3) refresh list/overview
    await dispatch(fetchExpensesByProject({ projectId: currentProjectId, page: 1, pageSize: 50 }));
    await dispatch(fetchExpenseOverview(currentProjectId));
    await refreshActivity();

    // close modal handled inside modal; if you removed it there, close here.
  };








  // Replaces or adds a bill for an expense
  const handleUploadBill = async ({ expenseId, file, billNumber }) => {
    // find the current expense object (prefer drawer details)
    const current =
      expenseDetails?._id === expenseId
        ? expenseDetails
        : (expenses || []).find(x => x._id === expenseId);

    // 1) delete old file if present (we can delete by https URL)
    const oldUrl = current?.bill?.fileUrl;
    if (oldUrl) {
      try {
        await deleteObject(ref(storage, oldUrl)); // works with https://firebasestorage.googleapis.com/...
      } catch (err) {
        console.warn('Could not delete previous bill (continuing):', err?.message || err);
      }
    }

    // 2) upload the new file
    const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
    const storagePath = `expenses/${currentProjectId}/${expenseId}/${Date.now()}.${ext}`;
    const fileRef = ref(storage, storagePath);
    await uploadBytes(fileRef, file, { contentType: file.type });
    const fileUrl = await getDownloadURL(fileRef);

    // 3) update DB
    await dispatch(
      uploadExpenseBill({
        expenseId,
        fileUrl,
        fileName: file.name,
        mimeType: file.type,
        billNumber,
      })
    );

    // 4) refresh UI
    await Promise.all([
      dispatch(fetchExpensesByProject({ projectId: currentProjectId, page: 1, pageSize: 50 })),
      dispatch(fetchExpenseOverview(currentProjectId)),
      dispatch(fetchExpenseById(expenseId)),
      refreshActivity()
    ]);

    setShowUploadModal(false);
  };





  // 👁 View expense
  const onViewExpense = (expense) => {
    setSelectedExpenseId(expense._id);
    dispatch(fetchExpenseById(expense._id));
  };

  const handleExportCSV = async () => {
    if (!currentProjectId) {
      notify('error', 'No project selected', 'Select a project before exporting.');
      return;
    }
    if (!startDate || !endDate) {
      notify('error', 'Missing date range', 'Select a date range before exporting the report.');
      return;
    }

    const params = new URLSearchParams({
      type: 'expenses',
      projectId: currentProjectId,
      from: startDate,
      to: endDate,
      detail: format || 'summary',
      category,
      format: 'csv',
    }).toString();

    const url = `${API_ROUTES.GENERATE_REPORT}?${params}`;

    try {
      const res = await axiosWithAuth.get(url, { responseType: 'blob' });

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const href = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = href;
      link.download = `expense-report-${startDate}-to-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(href);

      notify('success', 'CSV exported', 'Your expense report was downloaded.');
    } catch (err) {
      console.error('CSV export failed', err);
      notify(
        'error',
        'Export failed',
        err?.response?.data?.message || 'Failed to export CSV. Please try again.'
      );
    }
  };



  const handleExportPDF = () => {
    if (!currentProjectId) {
      notify('error', 'No project selected', 'Select a project before exporting.');
      return;
    }
    if (!startDate || !endDate) {
      notify('error', 'Missing date range', 'Select a date range before exporting the report.');
      return;
    }

    const params = new URLSearchParams({
      type: 'expenses',
      projectId: currentProjectId,
      from: startDate,
      to: endDate,
      detail: format || 'summary',
    }).toString();

    navigate(`/dashboard/expense-report-preview?${params}`, {
      state: { returnTo: `/dashboard/project/${currentProjectId}?tab=Expenses` }
    });

    setShowReportModal(false);

    notify('info', 'Opening report', 'Generating preview…');
  };






  // handlers
  const handleOpenEdit = (exp) => {
    setExpenseForEdit(exp);
    setShowEditModal(true);
  };


  const handleSubmitEdit = async ({ expenseId, updates }) => {
    // uses your thunk
    await dispatch(
      // import this from your slice
      editExpense({ expenseId, updates })
    );
    await Promise.all([
      dispatch(fetchExpensesByProject({ projectId: currentProjectId, page: 1, pageSize: 50 })),
      dispatch(fetchExpenseOverview(currentProjectId)),
      dispatch(fetchExpenseById(expenseId)), // 👈 refresh the drawer data with resolved names
      refreshActivity()
    ]);
  };

  const handleDelete = async (exp) => {
    const ok = window.confirm('Delete this expense? This cannot be undone.');
    if (!ok) return;

    try {
      await dispatch(deleteExpense(exp._id)).unwrap();
      await Promise.all([
        dispatch(fetchExpensesByProject({ projectId: currentProjectId, page: 1, pageSize: 50 })),
        dispatch(fetchExpenseOverview(currentProjectId)),
        refreshActivity(),
      ]);

      if (selectedExpenseId === exp._id) setSelectedExpenseId(null);

      notify('success', 'Deleted', 'Expense deleted successfully.');
    } catch (e) {
      notify('error', 'Delete failed', e?.message || 'Could not delete expense.');
    }
  };







  const handleDeleteBill = async (expense) => {
  if (!expense?.bill?.fileUrl) return;

  const ok = window.confirm('Remove the bill file from this expense?');
  if (!ok) return;

  try {
    await deleteObject(ref(storage, expense.bill.fileUrl)).catch(() => {});
    await dispatch(deleteExpenseBill(expense._id)).unwrap();

    await Promise.all([
      dispatch(fetchExpensesByProject({ projectId: currentProjectId, page: 1, pageSize: 50 })),
      dispatch(fetchExpenseOverview(currentProjectId)),
      dispatch(fetchExpenseById(expense._id)),
      refreshActivity(),
    ]);

    notify('success', 'Bill removed', 'Bill file removed successfully.');
  } catch (e) {
    notify('error', 'Remove failed', e?.message || 'Could not remove bill.');
  }
};


  const INR = (n) => `₹${(Number(n || 0)).toLocaleString('en-IN')}`;
  const fmtWhen = (iso) =>
    new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  const typeMeta = {
    expense_create: { label: 'Added', className: 'pill-added', icon: '➕' },
    expense_bill: { label: 'Bill', className: 'pill-bill', icon: '🧾' },
    expense_edit: { label: 'Edited', className: 'pill-edited', icon: '✏️' },
    expense_delete: { label: 'Deleted', className: 'pill-deleted', icon: '🗑' },
  };

  const activityLine = (item) => {
    const m = item.metadata || {};
    if (item.type === 'expense_bill') {
      // Prefer bill number or file name if present
      if (m.billNumber) return `Bill #${m.billNumber}`;
      if (m.fileName) return `Bill: ${m.fileName}`;
      return 'Bill uploaded';
    }
    // Default: show amount + (category)
    const parts = [];
    if (m.amount != null) parts.push(INR(m.amount));
    if (m.category) parts.push(`(${m.category})`);
    return parts.join(' ');
  };


  // ✅ HARD GATE: block first paint to kill flashing
  if (initialLoad) {
    return (
      <div className="expense-boot-loading">
        <div className="expense-boot-loading-card">
          <div className="expense-spinner" />
          <p>Loading expenses…</p>
        </div>
      </div>
    );
  }

  const hasNoExpenses = (expenses?.length ?? 0) === 0; // no need to check loading now



  return (
    <div className="expenses-wrapper">
      {hasNoExpenses ? (
        <EmptyState
          variant="expense"
          icon={<FaPlus />}
          title="No expenses yet"
          subtitle="Start by logging your first expense to track site spending."
          image={expenseIllustration}
          imageAlt="No expenses"
          ctaLabel="Add Expense"
          onCta={() => setShowModal(true)}
          disabled={!currentProjectId}
        />
      ) : (
        <>
          <div className="expense-tab-header-panel">
            <div className="expense-tab-metrics-box">
              <div className="expense-tab-metric">
                <p>Total Expenses</p>
                <h2>{overviewLoading ? '—' : INR(overview?.totalExpenses ?? 0)}</h2>
              </div>
              {/* <div className="expense-tab-metric-divider" /> */}
              {/* <div className="expense-tab-metric">
                <p>Bills Uploaded</p>
                 <h2>{overviewLoading ? '—' : (overview?.pendingBills ?? 0)}</h2>
              </div> */}
              <div className="expense-tab-metric-divider" />
              <div className="expense-tab-metric">
                <p>Pending Bills</p>
                <h2>{overviewLoading ? '—' : (overview?.pendingBills ?? 0)}</h2>
              </div>
              <div className="expense-tab-metric-divider" />
              <div className="expense-tab-metric">
                <p>Top Category</p>
                <h2>
                  {overviewLoading
                    ? '—'
                    : (overview?.byCategory?.[0]
                      ? `${overview.byCategory[0].category} (${INR(overview.byCategory[0].total)})`
                      : '—')}
                </h2>
              </div>
            </div>

            <div className="expense-tab-quick-actions">
              <button className="expense-tab-quick-btn" onClick={() => setShowModal(true)}>
                <FaPlus />
                <span>Add Expense</span>
              </button>
              <button className="expense-tab-quick-btn" onClick={() => setShowReportModal(true)}>
                <FaFileAlt />
                <span>View Report</span>
              </button>
            </div>
          </div>


          <div className="expense-content-wrapper">

            <div className="expense-tab-table-wrapper">
              <div className="expense-tab-table-header">
                <h3>Expense</h3>
                <div className="expense-tab-table-controls">
                  <input
                    type="text"
                    placeholder="Search Expense"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                    <option value="">Sort By</option>
                    <option value="date">Date</option>
                    <option value="amount">Amount</option>
                    <option value="category">Category</option>
                  </select>
                </div>
              </div>

              {/* Desktop Table */}
              <table className="expense-tab-table desktop-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Notes</th>
                    <th>Bill</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredExpenses || []).map((e) => (
                    <tr key={e._id}>
                      <td>{fmtDate(e.date)}</td>
                      <td>{e.category}</td>
                      <td>{INR(e.amount)}</td>
                      <td>{e.notes}</td>
                      {/* Desktop Table Bill cell */}
                      <td>
                        {e?.bill?.fileUrl ? (
                          <a
                            href={e.bill.fileUrl}
                            className="icon-btn"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="View bill"
                            title="View bill"
                          >
                            <FaEye size={16} />
                          </a>
                        ) : (
                          <button
                            className="icon-btn icon-btn--ghost"
                            onClick={() => {
                              setExpenseForUpload(e);
                              setShowUploadModal(true);
                            }}
                            aria-label="Upload bill"
                            title="Upload bill"
                            type="button"
                          >
                            <FaUpload size={16} />
                          </button>
                        )}
                      </td>

                      <td>
                        <div className="expense-actions">
                          <button className="view-btn" onClick={() => onViewExpense(e)}>View</button>
                          <button className="edit-btn" onClick={() => handleOpenEdit?.(e)}>✎</button>
                          <button className="delete-btn" onClick={() => handleDelete?.(e)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Cards */}
              <div className="expense-mobile-cards">
                {(filteredExpenses || []).map((e) => (
                  <div className="expense-card" key={e._id}>
                    <div className="expense-card-row"><strong>Date:</strong> {fmtDate(e.date)}</div>
                    <div className="expense-card-row"><strong>Category:</strong> {e.category}</div>
                    <div className="expense-card-row"><strong>Amount:</strong> {INR(e.amount)}</div>
                    <div className="expense-card-row"><strong>Notes:</strong> {e.notes || '—'}</div>
                    {/* Mobile Cards Bill row */}
                    <div className="expense-card-row">
                      <strong>Bill:</strong>{' '}
                      {e?.bill?.fileUrl ? (
                        <a
                          href={e.bill.fileUrl}
                          target="_blank"
                          className="icon-btn"
                          rel="noreferrer"
                          aria-label="View bill"
                          title="View bill"
                        >
                          <FaEye size={16} />
                        </a>
                      ) : (
                        <button
                          className="icon-btn icon-btn--ghost"
                          onClick={() => { setExpenseForUpload(e); setShowUploadModal(true); }}
                          aria-label="Upload bill"
                          title="Upload bill"
                          type="button"
                        >
                          <FaUpload size={16} />
                        </button>
                      )}
                    </div>

                    <div className="expense-card-actions">
                      <button className="view-btn" onClick={() => onViewExpense(e)}>👁</button>
                      <button className="edit-btn">✎</button>
                      <button className="delete-btn">🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="expense-recent-activity">
              <h4>Recent Activity</h4>

              {expenseActivityLoading && expenseActivity.length === 0 ? (
                <div className="activity-empty">Loading...</div>
              ) : expenseActivity.length === 0 ? (
                <div className="activity-empty">No activity yet.</div>
              ) : (
                <div className="activity-list">
                  {expenseActivity.map((it) => {
                    const t = typeMeta[it.type] || { label: it.type, className: 'pill-default', icon: '•' };
                    return (
                      <div key={it.id} className="activity-item">
                        <span className={`pill ${t.className}`}>{t.icon} {t.label}</span>
                        <span className="activity-text">{activityLine(it)}</span>
                        {it.userName && <span className="activity-user"> • {it.userName}</span>}
                        <span className="activity-time"> — {fmtWhen(it.createdAt)}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {expenseActivityNextCursor && (
                <button
                  className={`activity-load-more ${expenseActivityLoading ? 'is-loading' : ''}`}
                  disabled={expenseActivityLoading}
                  aria-busy={expenseActivityLoading}
                  onClick={() =>
                    dispatch(fetchMoreExpenseActivity({
                      projectId: currentProjectId,
                      cursor: expenseActivityNextCursor,
                      limit: 6
                    }))
                  }
                >
                  {expenseActivityLoading ? (
                    <>
                      <span className="spinner" aria-hidden="true"></span>
                      Loading
                    </>
                  ) : (
                    <>
                      {/* you already have react-icons; if not, remove the span below */}
                      <span className="chev" aria-hidden="true">▾</span>
                      Load more
                    </>
                  )}
                </button>
              )}

            </div>

          </div>


        </>
      )}
      {/* ➕ Add Expense Modal */}
      {showModal && (
        <AddExpenseModal
          onClose={() => setShowModal(false)}
          onSubmit={handleAddExpense}
          submitting={submitting}
        />
      )}



      {/* Drawer */}
      {selectedExpenseId && (
        <ViewExpenseDrawer
          expense={!expenseDetailsLoading ? expenseDetails : null}
          onClose={() => {
            setSelectedExpenseId(null);
            // optional: also clear details in Redux (see step 2)
            dispatch(clearExpenseDetails());
          }}
          onUploadBill={(expense) => {
            setExpenseForUpload(expense);
            setShowUploadModal(true);
          }}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
          onDeleteBill={handleDeleteBill}
        />
      )}


      {showEditModal && (
        <EditExpenseModal
          show={showEditModal}
          expense={expenseForEdit || expenseDetails} // prefer the one from Redux if you want
          onClose={() => setShowEditModal(false)}
          onSubmit={handleSubmitEdit}
          submitting={submitting}
        />
      )}


      {showUploadModal && (
        <UploadBillModal
          expense={expenseForUpload}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadBill} // now expects { expenseId, file, billNumber? }
        />
      )}


      {showReportModal && (
        <ViewReportModal
          show={showReportModal}
          onClose={() => setShowReportModal(false)}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          category={category}
          setCategory={setCategory}
          format={format}
          setFormat={setFormat}
          handleExportCSV={handleExportCSV}
          handleExportPDF={handleExportPDF}
        />

      )}




    </div>
  );
};

export default Expenses;
